import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { supabase } from "../../lib/supabaseClient";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { setNickname } from "../../features/nickname/nicknameSlice";
import { v4 as uuidv4 } from "uuid";
import defaultUserAvatar from "../../assets/defaultuser-avatar.svg";
import LoginRequiredModal from "../../components/Modals/LoginRequiredModal/LoginRequiredModal";
import { useGetWatchPartyQuery, useJoinWatchPartyMutation, useLeaveWatchPartyMutation } from "../../services/api";
import ChatMessage from "../../components/ChatMessage/ChatMessage";

type WatchParty = {
  id: string;
  host_user_id: string;
  imdb_id: string;
  title: string;
  type: string;
  scheduled_time: string;
  end_time?: string | null;
  description: string | null;
  max_participants: number;
  status: string;
  created_at: string;
  is_private?: boolean;
  invite_token?: string | null;
};

type Participant = {
  id: string;
  user_id: string;
  joined_at: string;
  profile?: {
    display_name: string;
    username: string;
    avatar_url: string;
  };
};

type Message = {
  id: string;
  content: string;
  nickname?: string;
  user_id?: string | null;
  created_at: string;
  profile?: {
    username: string;
    avatar_url: string;
  };
};

const generateUserId = () => `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const WatchPartyDetail: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authUser = useAppSelector((s) => s.auth.user);
  const { nickname } = useAppSelector((s: any) => s.nickname);
  const dispatch = useAppDispatch();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [usersCount, setUsersCount] = useState(0);
  const [channel, setChannel] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"details" | "participants">("details");
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // RTK Query
  const { data: party, isLoading: loading, refetch } = useGetWatchPartyQuery({ partyId: partyId!, userId: authUser?.id }, { skip: !partyId });
  const [joinParty] = useJoinWatchPartyMutation();
  const [leaveParty] = useLeaveWatchPartyMutation();

  const isJoined = party?.is_joined || false;
  const canChat = authUser ? isJoined : false;
  const film = party?.film;
  const hostProfile = party?.host_profile;
  const participants = party?.participants || [];

  // Auto-join / Access Control
  useEffect(() => {
    if (!party) return;

    if (party.is_private) {
      const token = searchParams.get("token");
      
      if (authUser) {
        if (!isJoined) {
          if (token && token === party.invite_token) {
             // Auto-join
             joinParty({ partyId: party.id, userId: authUser.id })
               .unwrap()
               .then(() => {
                 navigate(`/watch-party/${party.id}`, { replace: true });
               })
               .catch(() => {
                 // ignore error
               });
          } else {
            setAccessDenied(true);
          }
        }
      } else {
        // Anonymous
        if (!token || token !== party.invite_token) {
          setAccessDenied(true);
        }
      }
    }
  }, [party, authUser, isJoined, searchParams, joinParty, navigate]);

  // Load messages and setup real-time
  useEffect(() => {
    if (!partyId) return;

    let isMounted = true;

    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("party_chat_messages")
        .select("*")
        .eq("party_id", partyId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) {
        // console.error("Error fetching messages:", error);
        return;
      }

      if (isMounted) {
        // Fetch profiles for users
        const messagesWithProfiles = await Promise.all(
          (data || []).map(async (msg) => {
            if (msg.user_id) {
              const { data: profile } = await supabase.from("user_profiles").select("username, avatar_url").eq("id", msg.user_id).single();
              return { ...msg, profile };
            }
            return msg;
          })
        );

        setMessages(messagesWithProfiles.reverse());
      }
    };

    fetchMessages();

    // Setup real-time channel
    const ch = supabase.channel(`party-chat:${partyId}`, {
      config: { presence: { key: generateUserId() } },
    });

    ch.on("broadcast", { event: "message" }, (payload) => {
      const msg = payload.payload || payload;

      if (isMounted) {
        setMessages((prev) => {
          if (prev.some((m) => m.created_at === msg.created_at && m.content === msg.content)) {
            return prev;
          }
          const updated = [...prev, msg];

          // Scroll only within chat container, not the whole page
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 50);

          return updated.slice(-200);
        });
      }
    });

    ch.on("presence", { event: "sync" }, () => {
      const state = ch.presenceState();
      setUsersCount(Object.keys(state).length);
    });

    ch.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await ch.track({ online_at: new Date().toISOString() });
      }
    });

    setChannel(ch);

    return () => {
      isMounted = false;
      if (ch) {
        ch.untrack();
        ch.unsubscribe();
      }
    };
  }, [partyId]);



  // Join/Leave party
  // Join/Leave party
  const handleJoinLeave = async () => {
    if (!authUser) {
      alert("Please sign in to join");
      return;
    }

    if (!party) return;

    try {
      if (isJoined) {
        // Leave
        await leaveParty({ partyId: party.id, userId: authUser.id }).unwrap();
        navigate("/watch-parties");
      } else {
        // Join
        if (participants.length >= party.max_participants) {
          alert("This party is full!");
          return;
        }
        await joinParty({ partyId: party.id, userId: authUser.id }).unwrap();
      }
    } catch (err: any) {
      alert(err.message || "Failed to update party status");
    }
  };

  // Send message
  function ensureNickname(): string {
    if (!nickname) {
      const generated = `anon${Math.floor(Math.random() * 9000) + 1000}`;
      dispatch(setNickname(generated));
      return generated;
    }
    return nickname;
  }

  async function sendMessage() {
    if (!input.trim() || sendingMessage || !party || !channel) return;

    setSendingMessage(true);
    const timestamp = new Date().toISOString();

    let messagePayload: any;

    // Use username/display_name for auth users, or generated nickname for anonymous
    const messageNickname = authUser ? authUser.username || authUser.display_name || "user" : ensureNickname();

    messagePayload = {
      id: uuidv4(),
      content: input.trim(),
      nickname: messageNickname,
      user_id: authUser?.id || null,
      created_at: timestamp,
    };

    try {
      await channel.send({ type: "broadcast", event: "message", payload: messagePayload });

      const { error: insertError } = await supabase.from("party_chat_messages").insert([
        {
          party_id: partyId,
          content: messagePayload.content,
          nickname: messagePayload.nickname,
          user_id: messagePayload.user_id,
        },
      ]);

      if (insertError) {
        // console.error("Error inserting message:", insertError);
        throw insertError;
      }

      setMessages((prev) => [...prev, messagePayload].slice(-200));
      setInput("");
      inputRef.current?.focus();
    } catch (err) {
      // console.error("Send error:", err);
      alert("Failed to send message");
    }

    setSendingMessage(false);
  }

  const getThumbnail = () => {
    if (!film) return "";
    if (film.thumbnails) {
      try {
        const thumbs = typeof film.thumbnails === "string" ? JSON.parse(film.thumbnails) : film.thumbnails;
        return thumbs[1]?.url || thumbs[0]?.url || film.primary_image;
      } catch {
        return film.primary_image;
      }
    }
    return film.primary_image;
  };

  const getTimeUntil = () => {
    if (!party) return "";
    const start = new Date(party.scheduled_time);
    const diff = start.getTime() - currentTime.getTime();

    if (diff < 0) return "Started";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Show seconds only if under 1 hour
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${seconds}s`;
  };



  // Update countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-950">
        <div className="text-white text-xl">Loading party...</div>
      </div>
    );
  }

  if (!party || !film) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 p-4">
        <h2 className="text-2xl text-white mb-4">Party not found</h2>
        <button onClick={() => navigate("/watch-parties")} className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700">
          Back to Parties
        </button>
      </div>
    );
  }

  // Access denied for private parties
  if (accessDenied) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 p-4">
        <div className="max-w-md text-center space-y-4">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-3xl font-bold text-white mb-2">Private Party</h2>
          <p className="text-gray-400 mb-6">
            This is a private watch party. Only users with an invite link can join.
          </p>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-300">
              If you have an invite link, please use it to access this party.
            </p>
          </div>
          <button
            onClick={() => navigate("/watch-parties")}
            className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700 text-white font-semibold"
          >
            Browse Public Parties
          </button>
        </div>
      </div>
    );
  }

  const isFull = participants.length >= party.max_participants;
  const isHost = authUser?.id === party.host_user_id;
  const isFinished = Boolean(party.end_time && new Date() > new Date(party.end_time));
  const canShowInviteLink = party.is_private && (isHost || isJoined);

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/watch-party/${party.id}?token=${party.invite_token}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <>
      <div className="flex-1 bg-gray-950">
        <div className="max-w-7xl mx-auto p-4 lg:p-8">
          {/* Back Button */}
          <button onClick={() => navigate("/watch-parties")} className="flex items-center gap-2 text-red-500 hover:text-red-400 mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Parties
          </button>

          {/* Mobile Layout - Chat First */}
          <div className="lg:hidden space-y-4">
            {/* Party Chat - Top on Mobile */}
            <div className="bg-gray-900 border-2 border-red-600 rounded-xl flex flex-col h-[60vh]">
              <div className="p-4 border-b-2 border-red-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>Party Chat 💬</span>
                    {usersCount > 0 && <span className="text-sm text-green-400">👥 {usersCount}</span>}
                  </h3>
                  {canShowInviteLink && (
                    <button
                      onClick={copyInviteLink}
                      className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded transition-colors"
                    >
                      {copySuccess ? "✓" : "🔗"}
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 custom-scrollbar"
              >
                <div className="space-y-2">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((m, i) => <ChatMessage key={i} message={m} />)
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="p-4 border-t-2 border-red-600">
                {canChat && !isFinished ? (
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type message..."
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white text-sm"
                      disabled={sendingMessage}
                    />
                      <button
                        onClick={sendMessage}
                        disabled={!input.trim() || sendingMessage}
                        className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-700 font-semibold text-white text-sm"
                      >
                        Send
                      </button>
                    </div>
                  ) : isFinished ? (
                    <div className="text-center text-gray-500 font-semibold">
                      This party has finished.
                    </div>
                  ) : (
                  <div className="text-center">
                    {authUser ? (
                      <button
                        onClick={handleJoinLeave}
                        disabled={isFull}
                        className="px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-700 font-semibold text-white text-sm"
                      >
                        {isFull ? "Party Full" : "Join Party to Chat"}
                      </button>
                    ) : (
                      <button
                        onClick={() => setLoginModalOpen(true)}
                        className="px-6 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold"
                      >
                        Sign in to join and chat or go to general chat room
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Tabs for Details/Participants */}
            <div className="bg-gray-900 border-2 border-red-600 rounded-xl overflow-hidden">
              <div className="flex border-b-2 border-red-600">
                <button
                  onClick={() => setActiveTab("details")}
                  className={`flex-1 py-3 font-semibold ${activeTab === "details" ? "bg-red-600 text-white" : "text-gray-400"}`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab("participants")}
                  className={`flex-1 py-3 font-semibold ${activeTab === "participants" ? "bg-red-600 text-white" : "text-gray-400"}`}
                >
                  Participants ({participants.length})
                </button>
              </div>

              <div className="p-4">
                {activeTab === "details" ? (
                  <div className="space-y-4">
                    <img src={getThumbnail()} alt={film.original_title} className="w-full h-48 object-cover rounded-lg" />

                    <h2 className="text-xl font-bold text-white">{film.original_title}</h2>

                    <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-lg p-4 text-center">
                      {isFinished ? (
                        <>
                          <div className="text-2xl font-bold text-white">Party Finished</div>
                          <div className="text-sm text-red-100">Ended at {new Date(party.end_time!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-white">{getTimeUntil()}</div>
                          <div className="text-sm text-red-100">until party starts</div>
                        </>
                      )}
                    </div>

                    <div>
                      <div className="text-sm text-gray-500">Starts</div>
                      <div className="text-white font-semibold text-sm">
                        {new Date(party.scheduled_time).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>

                    {party.description && (
                      <div>
                        <div className="text-sm text-gray-500">Description</div>
                        <div className="text-gray-300 text-sm">{party.description}</div>
                      </div>
                    )}

                    <div>
                      <div className="text-sm text-gray-500">Participants</div>
                      <div className="text-white font-semibold">
                        {participants.length} / {party.max_participants}
                      </div>
                    </div>

                    {authUser && !isHost && (
                      <button
                        onClick={handleJoinLeave}
                        disabled={(!isJoined && isFull) || isFinished}
                        className={`w-full py-3 rounded-lg font-semibold ${
                          isFinished
                            ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                            : isJoined
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : isFull
                            ? "bg-gray-800 text-gray-600 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700 text-white"
                        }`}
                      >
                        {isFinished ? "Ended" : isJoined ? "Leave Party" : isFull ? "Full" : "Join"}
                      </button>
                    )}

                    <button
                      onClick={() => navigate(`/chat/${film.imdb_id}`)}
                      className="w-full py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
                    >
                      Go to Film Chat
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                    {participants.map((p: any) => (
                      <button
                        key={p.id}
                        onClick={() => navigate(`/profile/${p.username}`)}
                        className="w-full flex items-center gap-3 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <img
                          src={p.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                          alt={p.username || "User"}
                          className="w-8 h-8 rounded-full border-2 border-red-600"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                          }}
                        />

                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-white font-semibold truncate text-sm">
                            {p.username || "Unknown"}

                            {p.user_id === party.host_user_id && <span className="ml-2 text-xs bg-red-600 px-2 py-0.5 rounded">HOST</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Layout - Original */}
          <div className="hidden lg:grid grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="bg-gray-900 border-2 border-red-600 rounded-xl overflow-hidden">
                <img src={getThumbnail()} alt={film.original_title} className="w-full h-64 object-cover" />
                <div className="p-4">
                  <h2 className="text-2xl font-bold text-white mb-2">{film.original_title}</h2>
                  <p className="text-gray-400 mb-4">
                    {film.type === "tvSeries" ? "TV Series" : "Movie"} • {film.start_year}
                    {film.average_rating && <span> • ⭐ {Number(film.average_rating).toFixed(1)}</span>}
                  </p>
                  <button
                    onClick={() => navigate(`/chat/${film.imdb_id}`)}
                    className="w-full py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Go to Film Chat
                  </button>
                </div>
              </div>

              {/* Party Details */}
              <div className="bg-gray-900 border-2 border-red-600 rounded-xl p-6 space-y-4">
                <h3 className="text-xl font-bold text-white">Party Details</h3>

                {/* Countdown */}
                <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-lg p-4 text-center">
                  {isFinished ? (
                    <>
                      <div className="text-3xl font-bold text-white">Party Finished</div>
                      <div className="text-sm text-red-100">Ended at {new Date(party.end_time!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-white">{getTimeUntil()}</div>
                      <div className="text-sm text-red-100">until party starts</div>
                    </>
                  )}
                </div>

                {/* Time */}
                <div>
                  <div className="text-sm text-gray-500 mb-1">Starts</div>
                  <div className="text-white font-semibold">
                    {new Date(party.scheduled_time).toLocaleString(undefined, {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>

                {/* Description */}
                {party.description && (
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Description</div>
                    <div className="text-gray-300">{party.description}</div>
                  </div>
                )}

                {/* Host */}
                {hostProfile && (
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Hosted by</div>
                    <div className="flex items-center gap-3">
                      <img
                        src={hostProfile.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                        alt={hostProfile.username}
                        className="w-10 h-10 rounded-full border-2 border-red-600"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                        }}
                      />
                      <div>
                        <div className="text-white font-semibold">{hostProfile.username}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Participants Count */}
                <div>
                  <div className="text-sm text-gray-500 mb-1">Participants</div>
                  <div className="text-white font-semibold">
                    {participants.length} / {party.max_participants} joined
                  </div>
                  {isFull && <div className="text-red-400 text-sm mt-1">Party is full!</div>}
                </div>

                {/* Join/Leave Button */}
                {authUser && !isHost && (
                  <button
                    onClick={handleJoinLeave}
                    disabled={(!isJoined && isFull) || isFinished}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      isFinished
                        ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                        : isJoined
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : isFull
                        ? "bg-gray-800 text-gray-600 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700 text-white"
                    }`}
                  >
                    {isFinished ? "Ended" : isJoined ? "Leave Party" : isFull ? "Party Full" : "Join Party"}
                  </button>
                )}

                {isHost && (
                  <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 text-yellow-400 text-sm">
                    You are the host of this party
                  </div>
                )}

                {/* Copy Invite Link for Private Parties */}
                {canShowInviteLink && (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 mb-1">Private Party Invite Link 🔒</p>
                        <p className="text-xs text-gray-500 font-mono truncate">
                          ...{party.invite_token?.slice(-8)}
                        </p>
                      </div>
                      <button
                        onClick={copyInviteLink}
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded transition-colors whitespace-nowrap w-full sm:w-auto"
                      >
                        {copySuccess ? "✓ Copied!" : "Copy Link"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Participants List */}
              <div className="bg-gray-900 border-2 border-red-600 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Participants ({participants.length})</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                  {participants.map((p: any) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/profile/${p.username}`)}
                      className="w-full flex items-center gap-3 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <img
                        src={p.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                        alt={p.username || "User"}
                        className="w-8 h-8 rounded-full border-2 border-red-600"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                        }}
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-white font-semibold truncate">
                          {p.username || "Unknown"}
                          {p.user_id === party.host_user_id && <span className="ml-2 text-xs bg-red-600 px-2 py-0.5 rounded">HOST</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Chat */}
            <div className="col-span-2 bg-gray-900 border-2 border-red-600 rounded-xl flex flex-col h-[calc(100vh-12rem)]">
              <div className="p-4 border-b-2 border-red-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>Party Chat 💬</span>
                    {usersCount > 0 && <span className="text-sm text-green-400">👥 {usersCount} online</span>}
                  </h3>
                  {canShowInviteLink && (
                    <button
                      onClick={copyInviteLink}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded transition-colors flex items-center gap-1"
                    >
                      {copySuccess ? (
                        <>
                          <span>✓</span>
                          <span className="hidden sm:inline">Copied!</span>
                        </>
                      ) : (
                        <>
                          <span>🔗</span>
                          <span className="hidden sm:inline">Copy Invite</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 custom-scrollbar"
              >
                <div className="space-y-2">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p className="text-lg">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((m, i) => <ChatMessage key={i} message={m} />)
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="p-4 border-t-2 border-red-600">
                {canChat && !isFinished ? (
                  <div className="flex gap-3">
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white"
                      disabled={sendingMessage}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!input.trim() || sendingMessage}
                      className="px-6 py-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-700 font-semibold text-white"
                    >
                      Send
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    {isFinished ? (
                      <div className="text-gray-500 font-semibold">This party has finished.</div>
                    ) : authUser ? (
                      <button
                        onClick={handleJoinLeave}
                        disabled={isFull}
                        className="px-6 py-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-700 font-semibold text-white"
                      >
                        {isFull ? "Party Full" : "Join Party to Chat"}
                      </button>
                    ) : (
                      <button
                        onClick={() => setLoginModalOpen(true)}
                        className="px-6 py-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-semibold"
                      >
                        Sign in to join and chat party or go to general chat room
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoginRequiredModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  );
};

export default WatchPartyDetail;
